import { ReactNode, useContext, useEffect, useState } from 'react';
import { alpha, Box, lighten, useTheme } from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { useIntl, IntlShape } from 'react-intl';
import { useTour } from 'src/contexts/TourContext';
import { UserContext } from 'src/contexts/userContext';
import { UserType } from 'src/interfaces/UserTypes';
import {
  buildSodiohomeCustomerTourSteps, buildSodiohomePartnerTourSteps, buildSodiohomeAdminTourSteps,
  buildSalimaxCustomerTourSteps, buildSalimaxPartnerTourSteps, buildSalimaxAdminTourSteps,
  buildSodistoregridCustomerTourSteps, buildSodistoregridPartnerTourSteps, buildSodistoregridAdminTourSteps,
  buildSalidomoCustomerTourSteps, buildSalidomoPartnerTourSteps, buildSalidomoAdminTourSteps
} from 'src/config/tourSteps';

import Sidebar from './Sidebar';
import Header from './Header';

interface SidebarLayoutProps {
  children?: ReactNode;
  language: string;
  onSelectLanguage: (item: string) => void;
}

function getTourSteps(pathname: string, userType: UserType, intl: IntlShape, isInsideInstallation: boolean): Step[] {
  const role = userType === UserType.admin ? 'admin'
    : userType === UserType.partner ? 'partner'
    : 'customer';

  if (pathname.includes('/sodiohome_installations')) {
    if (role === 'admin') return buildSodiohomeAdminTourSteps(intl, isInsideInstallation);
    if (role === 'partner') return buildSodiohomePartnerTourSteps(intl, isInsideInstallation);
    return buildSodiohomeCustomerTourSteps(intl, isInsideInstallation);
  }
  if (pathname.includes('/salidomo_installations')) {
    if (role === 'admin') return buildSalidomoAdminTourSteps(intl, isInsideInstallation);
    if (role === 'partner') return buildSalidomoPartnerTourSteps(intl, isInsideInstallation);
    return buildSalidomoCustomerTourSteps(intl, isInsideInstallation);
  }
  if (pathname.includes('/sodistoregrid_installations')) {
    if (role === 'admin') return buildSodistoregridAdminTourSteps(intl, isInsideInstallation);
    if (role === 'partner') return buildSodistoregridPartnerTourSteps(intl, isInsideInstallation);
    return buildSodistoregridCustomerTourSteps(intl, isInsideInstallation);
  }
  // Salimax (/installations/) and Sodistore Max (/sodistore_installations/)
  if (role === 'admin') return buildSalimaxAdminTourSteps(intl, isInsideInstallation);
  if (role === 'partner') return buildSalimaxPartnerTourSteps(intl, isInsideInstallation);
  return buildSalimaxCustomerTourSteps(intl, isInsideInstallation);
}

const SidebarLayout = (props: SidebarLayoutProps) => {
  const theme = useTheme();
  const intl = useIntl();
  const { runTour, stopTour } = useTour();
  const location = useLocation();
  const { currentUser } = useContext(UserContext);
  const [tourSteps, setTourSteps] = useState<Step[]>([]);
  const [tourReady, setTourReady] = useState(false);

  useEffect(() => {
    if (!runTour) {
      setTourReady(false);
      return;
    }
    // Delay to let child components render their tour target elements
    const timer = setTimeout(() => {
      const userType = currentUser?.userType ?? UserType.client;
      const isInsideInstallation = location.pathname.includes('/installation/');
      const steps = getTourSteps(location.pathname, userType, intl, isInsideInstallation);
      const filtered = steps.filter((step) => {
        if (typeof step.target === 'string') {
          return document.querySelector(step.target) !== null;
        }
        return true;
      });
      setTourSteps(filtered);
      setTourReady(true);
    }, 300);
    return () => clearTimeout(timer);
  }, [runTour, location.pathname, currentUser?.userType, intl]);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      stopTour();
    }
  };

  return (
    <>
      <Joyride
        steps={tourSteps}
        run={tourReady}
        callback={handleJoyrideCallback}
        continuous
        showSkipButton
        showProgress
        scrollToFirstStep
        disableOverlayClose
        styles={{
          options: {
            primaryColor: '#ffc04d',
            zIndex: 10000
          }
        }}
      />
      <Box
        sx={{
          flex: 1,
          height: '100%',

          '.MuiPageTitle-wrapper': {
            background:
              theme.palette.mode === 'dark'
                ? theme.colors.alpha.trueWhite[5]
                : theme.colors.alpha.white[50],
            marginBottom: `${theme.spacing(4)}`,
            boxShadow:
              theme.palette.mode === 'dark'
                ? `0 1px 0 ${alpha(
                    lighten(theme.colors.primary.main, 0.7),
                    0.15
                  )}, 0px 2px 4px -3px rgba(0, 0, 0, 0.2), 0px 5px 12px -4px rgba(0, 0, 0, .1)`
                : `0px 2px 4px -3px ${alpha(
                    theme.colors.alpha.black[100],
                    0.1
                  )}, 0px 5px 12px -4px ${alpha(
                    theme.colors.alpha.black[100],
                    0.05
                  )}`
          }
        }}
      >
        <div style={{ userSelect: 'none' }}>
          <Header
            language={props.language}
            onSelectLanguage={props.onSelectLanguage}
          />
        </div>
        <Sidebar />
        <Box
          sx={{
            position: 'relative',
            zIndex: 5,
            display: 'block',
            flex: 1,
            pt: `${theme.header.height}`,
            [theme.breakpoints.up('lg')]: {
              ml: '260px'
            }
          }}
        >
          <Box display="block">
            <Outlet />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SidebarLayout;
